import { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Sun, Moon, Lock } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { ThemeContext, useThemeProvider, useTheme } from '../hooks/useTheme';
import { MerchantContext, useMerchantProvider } from '../hooks/useMerchant';
import { CRYPTO_SETUP_PATH } from '../crypto/CryptoLockGuard';
import { supabase } from '@shared/lib/supabase';
import type { Profile } from '@shared/types';
import NotificationBell from '../components/NotificationBell';

interface NavItem {
  to: string;
  symbol: string;
  label: string;
  end: boolean;
}

type NavItemLockedFn = (item: NavItem) => boolean;

const HOME_NAV: NavItem[] = [
  { to: '/dashboard/home', symbol: '\u25B3', label: 'Home', end: true },
];

const CRYPTO_NAV: NavItem[] = [
  { to: '/dashboard/crypto', symbol: '\u25CE', label: 'Overview', end: true },
  { to: '/dashboard/crypto/orders', symbol: '\u03A3', label: 'Orders', end: false },
  { to: '/dashboard/crypto/wallet', symbol: '\u25C8', label: 'Wallet', end: false },
  { to: '/dashboard/crypto/providers', symbol: '\u2295', label: 'Providers', end: false },
  { to: '/dashboard/crypto/payouts', symbol: '\u039E', label: 'Payouts', end: false },
  { to: '/dashboard/crypto/invoices', symbol: '\u229E', label: 'Invoices', end: false },
  { to: '/dashboard/crypto/reports', symbol: '\u03A0', label: 'Reports', end: false },
];

const STRIPE_NAV: NavItem[] = [
  { to: '/dashboard/stripe', symbol: '\u25CE', label: 'Overview', end: true },
  { to: '/dashboard/stripe/orders', symbol: '\u03A3', label: 'Orders', end: false },
  { to: '/dashboard/stripe/payouts', symbol: '\u22A1', label: 'Payouts', end: false },
  { to: '/dashboard/stripe/invoices', symbol: '\u229E', label: 'Invoices', end: false },
  { to: '/dashboard/stripe/reports', symbol: '\u03A0', label: 'Reports', end: false },
];

const SQUARE_NAV: NavItem[] = [
  { to: '/dashboard/square', symbol: '\u25CE', label: 'Overview', end: true },
  { to: '/dashboard/square/orders', symbol: '\u03A3', label: 'Orders', end: false },
  { to: '/dashboard/square/payouts', symbol: '\u229E', label: 'Payouts', end: false },
  { to: '/dashboard/square/invoices', symbol: '\u2316', label: 'Invoices', end: false },
  { to: '/dashboard/square/reports', symbol: '\u03A0', label: 'Reports', end: false },
  { to: '/dashboard/square/webhooks', symbol: '\u21AF', label: 'Webhooks', end: false },
];

function NavSection({
  title,
  items,
  collapsed,
  isLocked,
}: {
  title: string;
  items: NavItem[];
  collapsed: boolean;
  isLocked?: NavItemLockedFn;
}) {
  return (
    <div className="mb-0.5">
      {!collapsed && title && (
        <div className="px-5 pt-3 pb-1.5">
          <span className="font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: 'var(--dash-text-muted)' }}>
            {title}
          </span>
        </div>
      )}
      {collapsed && title && <div className="mx-2.5 my-1.5 h-px" style={{ background: 'var(--dash-border)' }} />}
      {items.map((item) => {
        const { to, symbol, label, end } = item;
        const locked = isLocked?.(item) ?? false;

        if (locked) {
          return (
            <div
              key={to}
              aria-disabled="true"
              title={collapsed ? `${label} (locked)` : 'Set up your crypto wallet to unlock'}
              className={`flex items-center gap-2.5 ${collapsed ? 'justify-center mx-2 px-2' : 'mx-2 px-3'} py-2 text-[13px] font-sans rounded cursor-not-allowed select-none`}
              style={{ color: 'var(--dash-text-faint)', opacity: 0.5 }}
            >
              <span className="w-4 text-center font-mono text-[13px]">{symbol}</span>
              {!collapsed && (
                <>
                  <span className="flex-1">{label}</span>
                  <Lock size={11} aria-hidden="true" />
                </>
              )}
            </div>
          );
        }

        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={collapsed ? label : undefined}
            className={({ isActive }) =>
              `flex items-center gap-2.5 ${collapsed ? 'justify-center mx-2 px-2' : 'mx-2 px-3'} py-2 text-[13px] font-sans rounded transition-all duration-150 ${
                isActive
                  ? 'nav-active'
                  : 'nav-inactive'
              }`
            }
            style={({ isActive }) => ({
              color: isActive ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
              background: isActive ? 'var(--dash-active-bg)' : undefined,
            })}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              if (!el.classList.contains('nav-active')) {
                el.style.color = 'var(--dash-text-secondary)';
                el.style.background = 'var(--dash-hover-bg)';
              }
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              if (!el.classList.contains('nav-active')) {
                el.style.color = 'var(--dash-text-muted)';
                el.style.background = '';
              }
            }}
          >
            <span className="w-4 text-center font-mono text-[13px]">{symbol}</span>
            {!collapsed && label}
          </NavLink>
        );
      })}
    </div>
  );
}

function HeaderClock({ timezone }: { timezone: string }) {
  const [time, setTime] = useState('');

  useEffect(() => {
    function tick() {
      try {
        const now = new Date();
        const datePart = now.toLocaleDateString('en-US', {
          timeZone: timezone,
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        });
        const timePart = now.toLocaleTimeString('en-US', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
        setTime(`${datePart}  ${timePart}`);
      } catch {
        const now = new Date();
        setTime(now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) + '  ' + now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }));
      }
    }
    tick();
    const interval = setInterval(tick, 30000);
    return () => clearInterval(interval);
  }, [timezone]);

  return (
    <span className="font-mono text-[11px] tabular-nums" style={{ color: 'var(--dash-text-muted)' }}>{time}</span>
  );
}

function ThemeToggle() {
  const { theme, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      className="p-2 rounded transition-colors"
      style={{ color: 'var(--dash-text-muted)' }}
      onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; e.currentTarget.style.background = ''; }}
      title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}

function DashboardInner() {
  const { user, signOut } = useAuth();
  const { theme } = useTheme();
  const merchantValue = useMerchantProvider(user);
  const { solanaWalletAddress, merchantSettings, merchantLoaded } = merchantValue;
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setProfile(data as unknown as Profile);
      });
  }, [user]);

  const cryptoSectionLocked = merchantLoaded && !solanaWalletAddress;
  const isCryptoItemLocked: NavItemLockedFn = (item) =>
    cryptoSectionLocked && item.to !== CRYPTO_SETUP_PATH;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch {}
    navigate('/login', { replace: true });
  };

  const displayName = profile?.business_name || user?.email?.split('@')[0] || 'Merchant';
  const displayEmail = profile?.email || user?.email || '';
  const avatarUrl = profile?.avatar_url;
  const sidebarWidth = collapsed ? 'w-[56px]' : 'w-52';
  const timezone = merchantSettings?.timezone as string || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <MerchantContext.Provider value={merchantValue}>
    <div className={`dash-theme ${theme === 'light' ? 'dash-light' : ''} flex h-screen overflow-hidden`} style={{ background: 'var(--dash-bg)' }}>
      <aside
        className={`${sidebarWidth} flex flex-col shrink-0 transition-all duration-200`}
        style={{ background: 'var(--dash-sidebar)', borderRight: '1px solid var(--dash-border)' }}
      >
        <div className={`${collapsed ? 'px-2' : 'px-4'} h-12 flex items-center justify-between`} style={{ borderBottom: '1px solid var(--dash-border)' }}>
          {!collapsed && (
            <span className="font-mono tracking-exhibit text-xs font-medium uppercase" style={{ color: 'var(--dash-text-primary)' }}>
              BONESPAY
            </span>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="transition-colors p-1 rounded font-mono text-sm"
            style={{ color: 'var(--dash-text-muted)' }}
            onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; e.currentTarget.style.background = ''; }}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? '\u203A' : '\u2039'}
          </button>
        </div>

        <nav className="flex-1 py-2 overflow-y-auto dash-scrollbar-themed">
          <NavSection title="" items={HOME_NAV} collapsed={collapsed} />
          <div className="mx-2.5 my-1.5 h-px" style={{ background: 'var(--dash-border)' }} />
          <NavSection title="Crypto" items={CRYPTO_NAV} collapsed={collapsed} isLocked={isCryptoItemLocked} />
          <div className="mx-2.5 my-1.5 h-px" style={{ background: 'var(--dash-border)' }} />
          <NavSection title="Stripe" items={STRIPE_NAV} collapsed={collapsed} />
          <div className="mx-2.5 my-1.5 h-px" style={{ background: 'var(--dash-border)' }} />
          <NavSection title="Square" items={SQUARE_NAV} collapsed={collapsed} />
        </nav>

        <div style={{ borderTop: '1px solid var(--dash-border)' }}>
          <NavLink
            to="/dashboard/settings"
            end={false}
            title={collapsed ? 'Settings' : undefined}
            className={({ isActive }) =>
              `flex items-center gap-2.5 ${collapsed ? 'justify-center mx-2 px-2' : 'mx-2 px-3'} py-2 mt-2 text-[13px] font-sans rounded transition-all duration-150 ${isActive ? 'nav-active' : 'nav-inactive'}`
            }
            style={({ isActive }) => ({
              color: isActive ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
              background: isActive ? 'var(--dash-active-bg)' : undefined,
            })}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              if (!el.classList.contains('nav-active')) {
                el.style.color = 'var(--dash-text-secondary)';
                el.style.background = 'var(--dash-hover-bg)';
              }
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              if (!el.classList.contains('nav-active')) {
                el.style.color = 'var(--dash-text-muted)';
                el.style.background = '';
              }
            }}
          >
            <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3" />
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
            </svg>
            {!collapsed && 'Settings'}
          </NavLink>

          {!collapsed && (
            <div
              className="mx-3 mb-2 mt-1 px-3 py-2 rounded"
              style={{ background: 'var(--dash-hover-bg)', border: '1px solid var(--dash-border-light)' }}
            >
              <div className="font-sans text-[12px] truncate" style={{ color: 'var(--dash-text-tertiary)' }}>{displayName}</div>
              <div className="font-mono text-[10px] truncate" style={{ color: 'var(--dash-text-faint)' }}>{displayEmail}</div>
            </div>
          )}
          {collapsed && (
            <div className="flex justify-center mb-2 mt-1">
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center overflow-hidden"
                style={{ background: 'var(--dash-avatar-bg)', border: '1px solid var(--dash-avatar-border)' }}
                title={displayName}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-mono text-[10px]" style={{ color: 'var(--dash-text-muted)' }}>{displayName.charAt(0).toUpperCase()}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          className="h-12 flex items-center justify-between px-5 shrink-0"
          style={{ background: 'var(--dash-header)', borderBottom: '1px solid var(--dash-border)' }}
        >
          <HeaderClock timezone={timezone} />

          <div className="flex items-center gap-1">
            <ThemeToggle />
            {user && <NotificationBell merchantId={user.id} />}

            <div className="relative" ref={profileRef}>
              <button
                onClick={() => setProfileOpen(!profileOpen)}
                className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden transition-colors ml-1"
                style={{ background: 'var(--dash-avatar-bg)', border: '1px solid var(--dash-avatar-border)' }}
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-mono text-xs" style={{ color: 'var(--dash-text-muted)' }}>{displayName.charAt(0).toUpperCase()}</span>
                )}
              </button>

              {profileOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-48 rounded shadow-xl z-50 py-1"
                  style={{ background: 'var(--dash-overlay)', border: '1px solid var(--dash-border-solid)' }}
                >
                  <div className="px-3 py-2" style={{ borderBottom: '1px solid var(--dash-border)' }}>
                    <div className="font-sans text-[12px] truncate" style={{ color: 'var(--dash-text-tertiary)' }}>{displayName}</div>
                    <div className="font-mono text-[10px] truncate" style={{ color: 'var(--dash-text-muted)' }}>{displayEmail}</div>
                  </div>
                  <button
                    onClick={() => { setProfileOpen(false); navigate('/dashboard/settings'); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-sans transition-colors"
                    style={{ color: 'var(--dash-text-muted)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; e.currentTarget.style.background = ''; }}
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                    Settings
                  </button>
                  <button
                    onClick={() => { setProfileOpen(false); handleSignOut(); }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-[13px] font-sans transition-colors"
                    style={{ color: 'var(--dash-text-muted)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; e.currentTarget.style.background = ''; }}
                  >
                    <span className="w-3.5 text-center font-mono text-[12px]">{'\u23CF'}</span>
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto dash-scrollbar-themed" style={{ background: 'var(--dash-bg)', overscrollBehavior: 'contain' }}>
          <Outlet />
        </main>
      </div>
    </div>
    </MerchantContext.Provider>
  );
}

export default function DashboardLayout() {
  const themeValue = useThemeProvider();

  return (
    <ThemeContext.Provider value={themeValue}>
      <DashboardInner />
    </ThemeContext.Provider>
  );
}
